import { getApiClient } from "@punchup/utils/apiRegistry";
import { logException } from "@punchup/sentry";
import {
  VenueCarouselResponse,
  UpdateCarouselRequest,
  UpdateCarouselOrderRequest,
  UploadCarouselImageRequest,
  UploadCarouselImageResponse,
  UploadCarouselVideoRequest,
  UploadCarouselVideoResponse,
  AddCarouselShowsRequest,
  AddCarouselShowsResponse,
} from "@punchup/data/models";

interface ApiResponse<T> {
  data: T;
}

export const CAROUSEL_QUERY_KEY = "venuePageCarousel";

/**
 * Query key for the PUBLIC variant of the carousel data (public show
 * pipeline applied). Kept distinct from the manager key so a manager-route
 * refetch can never overwrite the filtered public data (or vice versa);
 * mutations invalidating [CAROUSEL_QUERY_KEY, venuePageId] still match this
 * key by prefix.
 */
export const publicCarouselQueryKey = (venuePageId: string | undefined) => [
  CAROUSEL_QUERY_KEY,
  venuePageId,
  "public",
];

/**
 * Get carousel configuration and items for a venue page (manager view:
 * unfiltered, so expired pinned items stay visible and removable)
 */
export async function getVenuePageCarousel(
  venuePageId: string
): Promise<VenueCarouselResponse> {
  try {
    const response = await getApiClient().get<
      ApiResponse<VenueCarouselResponse>
    >(`/api/v2/manager/venue-pages/${venuePageId}/carousel`);
    return response.data;
  } catch (error) {
    logException(error, {
      extra: { venuePageId },
      tags: { source: "fetchVenuePageCarousel" },
    });
    throw error;
  }
}

/**
 * Get carousel configuration and items for the PUBLIC venue page. The route
 * applies the public show pipeline (expired shows dropped, live Tixologi
 * sold-out status merged, password-protected/hidden events removed) and
 * requires no auth, so anonymous visitors can refetch it.
 */
export async function getPublicVenuePageCarousel(
  venuePageId: string
): Promise<VenueCarouselResponse> {
  try {
    const response = await getApiClient().get<
      ApiResponse<VenueCarouselResponse>
    >(`/api/v2/public/venue-pages/${venuePageId}/carousel`);
    return response.data;
  } catch (error) {
    logException(error, {
      extra: { venuePageId },
      tags: { source: "fetchPublicVenuePageCarousel" },
    });
    throw error;
  }
}

/**
 * Update carousel mode and items (full replace)
 */
export async function updateVenuePageCarousel(
  venuePageId: string,
  data: UpdateCarouselRequest
): Promise<VenueCarouselResponse> {
  try {
    const response = await getApiClient().put<
      ApiResponse<VenueCarouselResponse>
    >(`/api/v2/manager/venue-pages/${venuePageId}/carousel`, data);
    return response.data;
  } catch (error) {
    logException(error, {
      extra: { venuePageId, data },
      tags: { source: "updateVenuePageCarousel" },
    });
    throw error;
  }
}

/**
 * Update carousel item order (lightweight, for drag-and-drop)
 */
export async function updateCarouselOrder(
  venuePageId: string,
  data: UpdateCarouselOrderRequest
): Promise<{ success: boolean }> {
  try {
    const response = await getApiClient().patch<
      ApiResponse<{ success: boolean }>
    >(`/api/v2/manager/venue-pages/${venuePageId}/carousel/order`, data);
    return response.data;
  } catch (error) {
    logException(error, {
      extra: { venuePageId, data },
      tags: { source: "updateCarouselOrder" },
    });
    throw error;
  }
}

/**
 * Upload a new image to the carousel
 */
export async function uploadCarouselImage(
  venuePageId: string,
  data: UploadCarouselImageRequest
): Promise<UploadCarouselImageResponse> {
  try {
    const response = await getApiClient().post<
      ApiResponse<UploadCarouselImageResponse>
    >(`/api/v2/manager/venue-pages/${venuePageId}/carousel/images`, data);
    return response.data;
  } catch (error) {
    logException(error, {
      extra: { venuePageId, data },
      tags: { source: "uploadCarouselImage" },
    });
    throw error;
  }
}

/**
 * Delete an image from the carousel
 */
export async function deleteCarouselImage(
  venuePageId: string,
  imageId: string
): Promise<{ success: boolean }> {
  try {
    const response = await getApiClient().del<
      ApiResponse<{ success: boolean }>
    >(`/api/v2/manager/venue-pages/${venuePageId}/carousel/images/${imageId}`);
    return response.data;
  } catch (error) {
    logException(error, {
      extra: { venuePageId, imageId },
      tags: { source: "deleteCarouselImage" },
    });
    throw error;
  }
}

/**
 * Upload a new video to the carousel
 */
export async function uploadCarouselVideo(
  venuePageId: string,
  data: UploadCarouselVideoRequest
): Promise<UploadCarouselVideoResponse> {
  try {
    const response = await getApiClient().post<
      ApiResponse<UploadCarouselVideoResponse>
    >(`/api/v2/manager/venue-pages/${venuePageId}/carousel/videos`, data);
    return response.data;
  } catch (error) {
    logException(error, {
      extra: { venuePageId, data },
      tags: { source: "uploadCarouselVideo" },
    });
    throw error;
  }
}

/**
 * Delete a video from the carousel
 */
export async function deleteCarouselVideo(
  venuePageId: string,
  videoId: string
): Promise<{ success: boolean }> {
  try {
    const response = await getApiClient().del<
      ApiResponse<{ success: boolean }>
    >(`/api/v2/manager/venue-pages/${venuePageId}/carousel/videos/${videoId}`);
    return response.data;
  } catch (error) {
    logException(error, {
      extra: { venuePageId, videoId },
      tags: { source: "deleteCarouselVideo" },
    });
    throw error;
  }
}

/**
 * Bulk add shows to the carousel
 */
export async function addCarouselShows(
  venuePageId: string,
  data: AddCarouselShowsRequest
): Promise<AddCarouselShowsResponse> {
  try {
    const response = await getApiClient().post<
      ApiResponse<AddCarouselShowsResponse>
    >(`/api/v2/manager/venue-pages/${venuePageId}/carousel/shows`, data);
    return response.data;
  } catch (error) {
    logException(error, {
      extra: { venuePageId, data },
      tags: { source: "addCarouselShows" },
    });
    throw error;
  }
}

/**
 * Delete a show from the carousel
 */
export async function deleteCarouselShow(
  venuePageId: string,
  showId: string
): Promise<{ success: boolean }> {
  try {
    const response = await getApiClient().del<
      ApiResponse<{ success: boolean }>
    >(`/api/v2/manager/venue-pages/${venuePageId}/carousel/shows/${showId}`);
    return response.data;
  } catch (error) {
    logException(error, {
      extra: { venuePageId, showId },
      tags: { source: "deleteCarouselShow" },
    });
    throw error;
  }
}
